/**
 * Guided card answer controls — money / chips / detail (kept ≤200 lines).
 */
import type { ComponentProps } from 'react'
import AnswerMicButton from './AnswerMicButton'
import MoneyWordsConfirm, { saveEnabledForMoney } from './MoneyWordsConfirm'
import QuestionDetailForm from './QuestionDetailForm'
import { parseMoneyInput } from '../../../../lib/formatters'
import MoneyInput from '../../../../components/MoneyInput'
import type { GuidedQuestion } from '../../../../lib/costsMarginQuestions'

const OTHER_CHIPS = ['Materials', 'Equipment rental', 'Permits', 'Credit card', 'Dump fees', 'Nothing else']
const CHIP_TO_CATEGORY: Record<string, string> = {
  Materials: 'materials',
  'Equipment rental': 'equipment',
  Permits: 'permits',
  'Credit card': 'credit_card',
  'Dump fees': 'other',
}

type DetailProps = Omit<ComponentProps<typeof QuestionDetailForm>, 'busy' | 'onSave' | 'mode'> & {
  onSave: () => void
}

type Props = {
  question: GuidedQuestion
  amount: string
  setAmount: (v: string) => void
  text: string
  setText: (v: string) => void
  chip: string
  setChip: (v: string) => void
  setCategory: (v: string) => void
  showDetail: boolean
  wordsOk: boolean
  setWordsOk: (v: boolean) => void
  moneyKinds: boolean
  primaryEnabled: boolean
  busy: boolean
  listening: boolean
  speechOk: boolean
  onMicToggle: () => void
  onPrimary: () => void
  onSkip: () => void
  detailProps: DetailProps
}

export { OTHER_CHIPS, CHIP_TO_CATEGORY }

export default function GuidedAnswerBody(p: Props) {
  const dollars = parseMoneyInput(p.amount)
  return (
    <>
      <div className="cm-answer-box">
        {p.moneyKinds ? (
          <MoneyInput
            value={p.amount}
            onChange={(v) => { p.setAmount(v); p.setWordsOk(false) }}
            placeholder={p.question.kind === 'subcontract_amount' ? 'Example: 28068.00' : 'Enter amount'}
            aria-label="Answer"
            data-testid="cm-answer-input"
          />
        ) : (
          <input
            value={p.text}
            onChange={(e) => p.setText(e.target.value)}
            placeholder="Enter amount or description"
            aria-label="Answer"
            data-testid="cm-answer-input"
          />
        )}
        <AnswerMicButton listening={p.listening} disabled={!p.speechOk} onToggle={p.onMicToggle} />
        <button type="button" className="cm-primary" disabled={!p.primaryEnabled} onClick={p.onPrimary} data-testid="cm-answer-save">
          {p.question.kind === 'other_costs' || p.question.kind === 'sub_change_orders' ? 'Add' : 'Save'}
        </button>
      </div>
      {p.moneyKinds && (
        <MoneyWordsConfirm dollars={dollars} confirmed={p.wordsOk} onConfirmedChange={p.setWordsOk} />
      )}
      {p.question.kind === 'other_costs' && (
        <div className="cm-chips" aria-label="Common cost categories">
          {OTHER_CHIPS.map((c) => (
            <button
              key={c}
              type="button"
              className={`cm-chip${p.chip === c ? ' selected' : ''}`}
              onClick={() => {
                p.setChip(c)
                if (CHIP_TO_CATEGORY[c]) p.setCategory(CHIP_TO_CATEGORY[c])
                if (c === 'Nothing else') p.onSkip()
              }}
            >
              {c}
            </button>
          ))}
        </div>
      )}
      {p.showDetail && (p.question.kind === 'other_costs' || p.question.kind === 'sub_change_orders') && (
        <>
          <QuestionDetailForm
            mode={p.question.kind}
            {...p.detailProps}
            busy={p.busy || !saveEnabledForMoney(parseMoneyInput(p.amount), p.wordsOk)}
          />
          <MoneyWordsConfirm dollars={parseMoneyInput(p.amount)} confirmed={p.wordsOk} onConfirmedChange={p.setWordsOk} />
        </>
      )}
    </>
  )
}
